import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { searchMovies, getPopularMovies, formatViews } from "@/utils/movies";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [searched, setSearched] = useState(Boolean(keyword));

  useEffect(() => {
    setInput(keyword);
    setSearched(Boolean(keyword));
  }, [keyword]);

  const results = keyword ? searchMovies(keyword) : [];
  const hot = getPopularMovies(4);

  usePageMeta({
    title: keyword
      ? `“${keyword}” 搜索结果 - 热情的邻居影视内容`
      : "搜索影视 - 热情的邻居影视内容",
    description: keyword
      ? `在热情的邻居搜索“${keyword}”，共找到 ${results.length} 部相关影视作品，包含剧情简介与类别标签。`
      : "输入影视名称、导演或演员关键词，快速查找热情的邻居相关影视内容与剧情介绍。",
    keywords: "热情的邻居,影视搜索,剧情介绍,影视查找",
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const term = input.trim();
    if (!term) {
      setSearchParams(new URLSearchParams());
      setSearched(false);
      return;
    }
    setSearchParams({ q: term });
  };

  const clear = () => {
    setInput("");
    setSearchParams(new URLSearchParams());
    setSearched(false);
  };

  return (
    <div className="w-full px-4 md:px-10 py-8 md:py-12">
      <div className="max-w-7xl mx-auto">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-6">
          <Link to="/" className="hover:text-accent-500 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-200">搜索</span>
        </nav>

        <div className="bg-background-100 border border-background-200/60 rounded-lg p-6 md:p-10">
          <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-50">搜索影视内容</h1>
          <p className="mt-3 text-sm text-foreground-400">
            支持按影视名称、导演、演员或地区关键词搜索，例如“热情的邻居”“悬疑”“古装”。
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row items-stretch gap-3">
            <div className="flex-1 flex items-center gap-3 bg-background-50 border border-background-300/60 rounded-md px-4 h-12">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入关键词开始搜索"
                className="flex-1 bg-transparent outline-none text-sm md:text-base text-foreground-100 placeholder:text-foreground-600"
              />
              {input && (
                <button
                  type="button"
                  onClick={clear}
                  aria-label="清除关键词"
                  className="w-6 h-6 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-100 hover:bg-background-200 cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-12 px-8 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="flex flex-wrap items-center gap-2 mt-5">
            <span className="text-xs text-foreground-600">热门搜索：</span>
            {["热情的邻居", "悬疑", "古装", "综艺", "治愈"].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  setInput(tag);
                  setSearchParams({ q: tag });
                }}
                className="px-3 py-1 rounded-full bg-background-200/70 text-foreground-300 text-xs hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {searched && (
          <div className="mt-8">
            <div className="flex items-center justify-between gap-4 mb-6">
              <p className="text-sm text-foreground-400">
                关键词 <span className="text-accent-500 font-semibold">“{keyword}”</span> 共找到{" "}
                <span className="text-accent-500 font-semibold">{results.length}</span> 条结果
              </p>
              <button
                type="button"
                onClick={clear}
                className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-foreground-100 whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-close-circle-line" />
                清除关键词
              </button>
            </div>

            {results.length > 0 ? (
              <div className="flex flex-col gap-3">
                {results.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/detail/${movie.id}`}
                    className="group flex flex-col sm:flex-row gap-4 p-3 bg-background-100 border border-background-200/60 rounded-lg hover:border-primary-500/60 transition-colors"
                  >
                    <span className="w-full sm:w-[96px] h-[140px] sm:h-[144px] shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={movie.poster}
                        alt={`${movie.title} 封面`}
                        title={`${movie.title} 影视内容`}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="text-base md:text-lg font-semibold text-foreground-50 group-hover:text-accent-500 transition-colors">
                          {movie.title}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-foreground-500">
                          <i className="ri-star-fill text-accent-500" />
                          {movie.rating.toFixed(1)}
                        </span>
                      </span>
                      <span className="block text-xs text-foreground-500 mt-1.5">
                        {movie.type} · {movie.region} · {movie.year} · {movie.status}
                      </span>
                      <span className="block text-sm text-foreground-400 mt-2 line-clamp-2 leading-relaxed">
                        {movie.summary}
                      </span>
                      <span className="flex flex-wrap gap-2 mt-2.5">
                        {movie.genres.map((genre) => (
                          <span
                            key={genre}
                            className="px-2.5 py-0.5 rounded-full bg-secondary-100/70 text-secondary-900 text-xs whitespace-nowrap"
                          >
                            {genre}
                          </span>
                        ))}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center text-center bg-background-100 border border-background-200/60 rounded-lg py-16 px-6">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-4">
                  <i className="ri-search-eye-line text-3xl" />
                </span>
                <h3 className="font-heading font-bold text-lg text-foreground-100">未找到相关影视内容</h3>
                <p className="text-sm text-foreground-500 mt-2 max-w-md">
                  换个关键词试试，或者看看下面这些热门影视作品。
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 w-full max-w-3xl">
                  {hot.map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/detail/${movie.id}`}
                      className="group p-2 bg-background-50 border border-background-200/60 rounded-lg hover:border-accent-500/60 transition-colors text-left"
                    >
                      <span className="block w-full aspect-[2/3] rounded-md overflow-hidden bg-background-200">
                        <img
                          src={movie.poster}
                          alt={`${movie.title} 封面`}
                          title={`${movie.title} 影视内容`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="block text-xs font-medium text-foreground-100 mt-2 truncate group-hover:text-accent-500 transition-colors">
                        {movie.title}
                      </span>
                      <span className="block text-[11px] text-foreground-500 mt-0.5">
                        {formatViews(movie.views)} 浏览
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!searched && (
          <div className="mt-8">
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50 flex items-center gap-2 mb-5">
              <span className="w-1 h-5 rounded-full bg-accent-500 inline-block" />
              热门推荐
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
              {hot.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group bg-background-100 border border-background-200/60 rounded-lg overflow-hidden hover:border-primary-500/60 transition-colors"
                >
                  <span className="block w-full aspect-[2/3] overflow-hidden bg-background-200">
                    <img
                      src={movie.poster}
                      alt={`${movie.title} 封面`}
                      title={`${movie.title} 影视内容`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </span>
                  <span className="block p-3">
                    <span className="block text-sm font-semibold text-foreground-50 truncate group-hover:text-accent-500 transition-colors">
                      {movie.title}
                    </span>
                    <span className="block text-xs text-foreground-500 mt-1 truncate">
                      {movie.region} · {movie.year}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}